import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Edit, Trash2, Plus, Search, ToggleLeft, ToggleRight, Pencil, Zap } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { DynamicTable } from '../common/DynamicTable';

interface MensagemRapida {
  id: string;
  chave: string;
  mensagem: string;
  ativa: boolean;
  usuarioId?: string;
  usuario?: {
    id: string;
    nome: string;
    email: string;
  };
  categoria?: {
    id: string;
    nome: string;
    ordem: number;
  };
  anexos?: Array<{ nome: string; url: string; tipo: string }>;
  dataCriacao: string;
  dataAtualizacao?: string;
}

interface MensagensRapidasTableProps {
  mensagens: MensagemRapida[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: string, order: 'asc' | 'desc') => void;
  onSearch: (term: string) => void;
  onEdit: (mensagem: MensagemRapida) => void;
  onDelete: (mensagem: MensagemRapida) => void;
  canEdit: boolean;
}

export const MensagensRapidasTable: React.FC<MensagensRapidasTableProps> = ({
  mensagens,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onEdit,
  onDelete,
  canEdit,
}) => {
  const columns = [
    {
      id: 'chave' as Extract<keyof MensagemRapida, string>,
      label: 'Chave',
      sortable: true,
      format: (value: string) => (
        <div className="flex items-center">
          <Zap className="h-4 w-4 mr-2 text-blue-500" />
          <code className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-sm font-mono">
            /{value}
          </code>
        </div>
      )
    },
    {
      id: 'mensagem' as Extract<keyof MensagemRapida, string>,
      label: 'Mensagem',
      format: (value: string, row: MensagemRapida) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 dark:text-gray-100 truncate" title={value}>
            {value}
          </p>
          {row.anexos && row.anexos.length > 0 && (
            <p className="text-xs text-blue-500 mt-1">
              {row.anexos.length} anexo(s)
            </p>
          )}
        </div>
      )
    },
    {
      id: 'ativa' as Extract<keyof MensagemRapida, string>,
      label: 'Status',
      align: 'center' as const,
      format: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {value ? 'Ativa' : 'Inativa'}
        </span>
      )
    },
    {
      id: 'dataCriacao' as Extract<keyof MensagemRapida, string>,
      label: 'Criado em',
      format: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    {
      id: 'actions' as any,
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: MensagemRapida) => (
        <div className="flex justify-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
            disabled={!canEdit}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
            disabled={!canEdit}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={mensagens}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      mobileCards
      searchPlaceholder="Pesquisar mensagens rápidas..."
      showSearch={true}
    />
  );
};
